import { useEffect, useState } from 'react';
import { PageHeader } from '@nimbitlabs/layouts';
import { Alert, AlertRule, alertsApi } from '@/lib/alerts';
import { Bell, BellOff, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PermissionGate } from '@nimbitlabs/features';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Tabs, TabsContent, TabsList, TabsTrigger, useToast, ErrorAlert } from '@nimbitlabs/ui';

export default function Alerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [alertsData, rulesData] = await Promise.all([
        alertsApi.getAlerts(),
        alertsApi.getAlertRules(),
      ]);
      setAlerts(alertsData);
      setRules(rulesData);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await alertsApi.acknowledgeAlert(alertId, 'current-user');
      toast({ title: 'Success', description: 'Alert acknowledged' });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to acknowledge alert',
        variant: 'destructive',
      });
    }
  };

  const handleResolve = async (alertId: string) => {
    try {
      await alertsApi.resolveAlert(alertId);
      toast({ title: 'Success', description: 'Alert resolved' });
      loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resolve alert',
        variant: 'destructive',
      });
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, string> = {
      critical: 'bg-destructive text-destructive-foreground',
      warning: 'bg-warning text-warning-foreground',
      info: 'bg-primary text-primary-foreground',
    };
    return variants[severity] || 'bg-muted';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <AlertTriangle className="h-4 w-4" />;
      case 'acknowledged':
        return <Clock className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading alerts...</div>
      </div>
    );
  }

  const activeAlerts = alerts.filter(a => a.status === 'active');
  const acknowledgedAlerts = alerts.filter(a => a.status === 'acknowledged');

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <ErrorAlert error={error} />
      <PageHeader
        title="Alerts & Notifications"
        description="Monitor and manage system alerts"
        actions={
          <PermissionGate permission="alerts:update">
            <Button onClick={() => navigate('/alerts/rules')} className="w-full sm:w-auto">
              <Bell className="h-4 w-4 mr-2" />
              Manage Rules
            </Button>
          </PermissionGate>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{activeAlerts.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Acknowledged</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{acknowledgedAlerts.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Alert Rules</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{rules.filter(r => r.enabled).length}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {rules.length} total
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active">
        <div className="w-full overflow-x-auto py-3 px-1 sm:px-0">
          <div className="inline-flex w-max min-w-full md:min-w-0 rounded-xl bg-card shadow-soft p-[4px]">
            <TabsList className="inline-flex h-12 items-center justify-start gap-2 rounded-xl bg-gradient-to-r from-muted/40 via-muted/30 to-muted/40 p-1.5 backdrop-blur-sm w-full">
            <TabsTrigger
            value="active"
            className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          >
            <span className="relative z-10">Active ({activeAlerts.length})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="acknowledged"
            className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          >
            <span className="relative z-10">Acknowledged ({acknowledgedAlerts.length})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="resolved"
            className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          >
            <span className="relative z-10">Resolved</span>
          </TabsTrigger>
          </TabsList>
          </div>
        </div>

        <TabsContent value="active" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-muted-foreground">Loading...</div>
            </div>
          ) : activeAlerts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BellOff className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No active alerts</p>
              </CardContent>
            </Card>
          ) : (
            activeAlerts.map(alert => (
              <Card key={alert.id} className="border-l-4" style={{
                borderLeftColor: alert.severity === 'critical' ? 'hsl(var(--destructive))' : 'hsl(var(--warning))'
              }}>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="space-y-1 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {getStatusIcon(alert.status)}
                        <CardTitle className="text-lg">{alert.ruleName}</CardTitle>
                        <Badge className={getSeverityBadge(alert.severity)}>
                          {alert.severity}
                        </Badge>
                      </div>
                      <CardDescription>
                        {alert.serviceName} • {alert.environmentName}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <PermissionGate permission="alerts:update">
                        <Button size="sm" variant="outline" onClick={() => handleAcknowledge(alert.id)} className="w-full sm:w-auto">
                          Acknowledge
                        </Button>
                      </PermissionGate>
                      <PermissionGate permission="alerts:update">
                        <Button size="sm" onClick={() => handleResolve(alert.id)} className="w-full sm:w-auto">
                          Resolve
                        </Button>
                      </PermissionGate>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{alert.message}</p>
                  <p className="text-xs text-muted-foreground">
                    Triggered {new Date(alert.triggeredAt).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="acknowledged" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-muted-foreground">Loading...</div>
            </div>
          ) : acknowledgedAlerts.map(alert => (
            <Card key={alert.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(alert.status)}
                      <CardTitle className="text-lg">{alert.ruleName}</CardTitle>
                      <Badge className={getSeverityBadge(alert.severity)}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <CardDescription>
                      {alert.serviceName} • {alert.environmentName}
                    </CardDescription>
                  </div>
                  <PermissionGate permission="alerts:update">
                    <Button size="sm" onClick={() => handleResolve(alert.id)}>
                      Resolve
                    </Button>
                  </PermissionGate>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-2">{alert.message}</p>
                <p className="text-xs text-muted-foreground">
                  Acknowledged by {alert.acknowledgedBy} at{' '}
                  {alert.acknowledgedAt && new Date(alert.acknowledgedAt).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="resolved">
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No resolved alerts to display</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
